import {
  createContext,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { ERROR_SESSION_EXPIRED } from 'app/shared/consts/error-codes';
import { ErrorNotification } from 'app/shared/types/errors.types';
import { isRetryableAuthError } from 'app/shared/utils/is-auth-error';

/**
 * Two distinct error channels — same shape, different severity and UX.
 *
 * • `errors` / `addError(queryKey, error)` / `hideErrors([queryKey])`
 *   — **Recoverable, dismissible errors.** Surfaced via the global
 *   `<ErrorModal>` mounted in `AppContentWrapper`. Use for soft failures
 *   that don't break the app: a save retry, "slot was just taken", an
 *   optional API that's slow. Each entry is keyed by a query/mutation
 *   key so the same key can be cleared after a successful retry.
 *
 * • `genericErrors` / `addGenericError(queryKey, error)` /
 *   `hideGenericErrors([queryKey])`
 *   — **Terminal errors.** The app cannot continue meaningfully (auth
 *   lost, foundational query failed, host iframe didn't pass apiUrl).
 *   The `<ErrorNavigator>` watches this channel and pushes the user to
 *   `/error`; the back button on the error page returns to the previous
 *   route once the underlying issue is gone. An auth failure arriving here
 *   has already spent its refresh-and-retry, so it is rewritten to
 *   `ERROR_SESSION_EXPIRED` — one place for every query hook, no per-hook
 *   mapping.
 *
 * Picking between them: if the user can keep using the current screen,
 * use `addError`. If they can't, use `addGenericError`.
 */
interface ErrorsContextValue {
  errors: Array<ErrorNotification>;
  hideErrors: (queryKey?: string) => void;
  addError: (queryKey: string, error: ErrorNotification) => void;
  genericErrors: Array<ErrorNotification>;
  hideGenericErrors: (queryKey?: string) => void;
  addGenericError: (queryKey: string, error: ErrorNotification) => void;
}

const ErrorsContext = createContext<ErrorsContextValue | null>(null);

export const ErrorsProvider = ({
  children,
}: PropsWithChildren): ReactElement => {
  // Terminal errors — consumed by <ErrorNavigator> to push to /error.
  const [genericErrors, setGenericErrors] = useState<{ queryKey: string, error: ErrorNotification }[]>([]);

  const hideGenericErrors = useCallback((queryKey?: string) => {
    if (queryKey) {
      setGenericErrors((prevErrors) => prevErrors.filter((error) => error.queryKey !== queryKey));
    } else {
      setGenericErrors([]);
    }
  }, []);

  const addGenericError = useCallback((queryKey: string, error: ErrorNotification) => {
    // Prepended because `/error` renders `genericErrors[0]`, and a session
    // that can be recovered beats a generic "Something went wrong".
    if (isRetryableAuthError(error.error)) {
      const sessionExpired = { queryKey, error: { ...error, errorCode: ERROR_SESSION_EXPIRED } };
      setGenericErrors((prevErrors) => [sessionExpired, ...prevErrors]);

      return;
    }

    setGenericErrors((prevErrors) => [...prevErrors, { queryKey, error }]);
  }, []);

  // Recoverable errors — consumed by <ErrorModal> as a dismissible popup.
  const [errors, setErrors] = useState<{ queryKey: string, error: ErrorNotification }[]>([]);

  const hideErrors = useCallback((queryKey?: string) => {
    if (queryKey) {
      setErrors((prevErrors) => prevErrors.filter((error) => error.queryKey !== queryKey));
    } else {
      setErrors([]);
    }
  }, []);

  const addError = useCallback((queryKey: string, error: ErrorNotification) => {
    setErrors((prevErrors) => [...prevErrors, { queryKey, error }]);
  }, []);

  const contextValue = useMemo(() => ({
    errors: errors.map((error) => error.error),
    hideErrors,
    addError,
    genericErrors: genericErrors.map((error) => error.error),
    hideGenericErrors,
    addGenericError,
  }), [genericErrors, errors, hideErrors, hideGenericErrors, addError, addGenericError]);

  return <ErrorsContext.Provider value={contextValue}>{children}</ErrorsContext.Provider>;
};

export function useErrors(): ErrorsContextValue {
  const context = useContext(ErrorsContext);
  if (!context) {
    throw new Error('useErrors must be used within a ErrorsProvider');
  }
  return context;
}
